import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../hooks/useAuth';
import { usePair } from '../hooks/usePair';
import { useModuleProgress } from '../hooks/useModuleProgress';
import { useMeetings } from '../hooks/useMeetings';
import { usePrayerRequests } from '../hooks/usePrayerRequests';
import { updatePairStatus } from '../supabase/api';

const { FiCalendar, FiBook, FiCheckCircle, FiClock, FiTrendingUp, FiUsers, FiTarget, FiHeart } = FiIcons;

const WEEK_MODULES = [
  { week: 1, title: 'Jesus is Your Salvation and Story', scripture: 'John 3:16-19' },
  { week: 2, title: 'You Are A New Creation', scripture: 'Galatians 2:20' },
  { week: 3, title: 'The Holy Spirit Points You To Jesus', scripture: 'John 16:13-14' },
  { week: 4, title: 'Surrender Your Struggles', scripture: '1 Corinthians 10:13' },
  { week: 5, title: 'You Are Called To Give Generously', scripture: '2 Corinthians 9:6-7' },
  { week: 6, title: 'Follow Jesus By Serving Others', scripture: 'Matthew 5:16' },
];

function Dashboard({ userRole }) {
  const { user } = useAuth();
  const { pair, loading: pairLoading } = usePair(user?.id, userRole);
  const { progress, completedCount, getWeekStatus, loading: progressLoading } = useModuleProgress(pair?.id);
  const { meetings, loading: meetingsLoading } = useMeetings(pair?.id, user?.id);
  const { prayers, loading: prayersLoading } = usePrayerRequests(pair?.id, user?.id);

  const [archiving, setArchiving] = useState(false);

  const totalWeeks = 6;
  const progressPercentage = (completedCount / totalWeeks) * 100;

  // Next upcoming meeting
  const today = new Date().toISOString().split('T')[0];
  const nextMeeting = meetings
    .filter((m) => m.scheduled_date >= today)
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0];

  const formatMeetingDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const formatTime = (t) => new Date(`2000-01-01T${t}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  const isLoading = pairLoading || progressLoading;

  const handleArchive = async () => {
    if (!pair) return;
    setArchiving(true);
    try {
      await updatePairStatus(pair.id, 'completed');
      window.location.reload(); // Refresh to clear context and show empty dashboard
    } catch (err) {
      console.error(err);
      setArchiving(false);
    }
  };

  const stats = [
    { icon: FiCheckCircle, label: 'Completed Weeks', value: isLoading ? '…' : `${completedCount}`, color: 'text-green-600 dark:text-green-400' },
    { icon: FiClock, label: 'Meetings Scheduled', value: meetingsLoading ? '…' : `${meetings.length}`, color: 'text-blue-600 dark:text-blue-400' },
    { icon: FiTrendingUp, label: 'Growth Score', value: isLoading ? '…' : `${Math.round(progressPercentage)}%`, color: 'text-primary' },
    { icon: FiHeart, label: 'Prayer Requests', value: prayersLoading ? '…' : `${prayers.length}`, color: 'text-red-500 dark:text-red-400' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-colors duration-200">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-3xl font-space font-bold text-gray-900 dark:text-white mb-2">
          Welcome back, {userRole === 'leader' ? 'Leader' : 'Participant'}!
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {userRole === 'leader'
            ? 'Guide your participant through their spiritual journey with confidence.'
            : 'Continue your discipleship journey with dedication and faith.'}
        </p>
      </motion.div>

      {/* Completion Banner */}
      {completedCount === totalWeeks && pair && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800/50 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4"
        >
          <div>
            <h2 className="text-xl font-space font-bold text-green-800 dark:text-green-300 flex items-center gap-2">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
              Journey Complete!
            </h2>
            <p className="text-green-700 dark:text-green-400 mt-1">
              {userRole === 'leader'
                ? `You and your participant have successfully completed all ${totalWeeks} weeks. You can now archive this journey to start a new one.`
                : `Congratulations! You have successfully completed all ${totalWeeks} weeks of your discipleship journey.`}
            </p>
          </div>
          {userRole === 'leader' && (
            <button
              onClick={handleArchive}
              disabled={archiving}
              className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap font-medium shadow-sm"
            >
              {archiving ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <SafeIcon icon={FiBook} className="w-4 h-4" />
              )}
              Archive & Start New
            </button>
          )}
        </motion.div>
      )}

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
      >
        {stats.map((stat, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                <p className="text-2xl font-space font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
              <SafeIcon icon={stat.icon} className={`w-8 h-8 ${stat.color}`} />
            </div>
          </div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Journey Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-space font-semibold text-gray-900 dark:text-white">Journey Progress</h2>
            <div className="flex items-center space-x-2 text-primary">
              <SafeIcon icon={FiTarget} className="w-5 h-5" />
              <span className="font-medium">
                {isLoading ? '…' : `${completedCount}/${totalWeeks} Complete`}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Overall Progress</span>
              <span className="text-sm font-medium text-primary">
                {isLoading ? '…' : `${Math.round(progressPercentage)}%`}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: isLoading ? '0%' : `${progressPercentage}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="bg-gradient-to-r from-primary to-accent h-3 rounded-full"
              />
            </div>
          </div>

          {/* Week List */}
          <div className="space-y-4">
            {WEEK_MODULES.map((module) => {
              const status = getWeekStatus(module.week);
              const isCompleted = status === 'completed';
              const isInProgress = status === 'in_progress';

              return (
                <Link
                  key={module.week}
                  to={`/week/${module.week}`}
                  className="block p-4 border border-gray-100 dark:border-gray-700 rounded-lg hover:border-primary dark:hover:border-primary hover:shadow-md transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCompleted ? 'bg-green-100 dark:bg-green-900/30' : isInProgress ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        {isCompleted ? (
                          <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 dark:text-green-400" />
                        ) : isInProgress ? (
                          <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{module.week}</span>
                        )}
                      </div>
                      <div>
                        <h3 className={`font-medium group-hover:text-primary transition-colors ${
                          isCompleted ? 'text-gray-500 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'
                        }`}>
                          Week {module.week}: {module.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{module.scripture}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isCompleted && (
                        <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">Done</span>
                      )}
                      {isInProgress && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full font-medium">In Progress</span>
                      )}
                      <SafeIcon icon={FiBook} className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-primary" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </motion.div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Next Meeting */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
          >
            <div className="flex items-center space-x-3 mb-4">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary" />
              <h3 className="font-space font-semibold text-gray-900 dark:text-white">Next Meeting</h3>
            </div>

            {meetingsLoading ? (
              <div className="text-center py-6">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : nextMeeting ? (
              <div className="p-3 bg-primary bg-opacity-5 dark:bg-opacity-20 rounded-lg border border-primary border-opacity-20 dark:border-opacity-30">
                <p className="font-medium text-gray-900 dark:text-white">{formatMeetingDate(nextMeeting.scheduled_date)}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{formatTime(nextMeeting.scheduled_time)}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">📍 {nextMeeting.location}</p>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 dark:text-gray-400 mb-4 text-sm">No upcoming meetings</p>
                <Link
                  to="/meetings"
                  className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm"
                >
                  Schedule Meeting
                </Link>
              </div>
            )}
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
          >
            <h3 className="font-space font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link to="/meetings" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Schedule Meeting</span>
              </Link>
              
              {/* Link to next incomplete week */}
              {(() => {
                const nextWeek = WEEK_MODULES.find(m => getWeekStatus(m.week) !== 'completed');
                return (
                  <Link to={`/week/${nextWeek?.week || 1}`} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <SafeIcon icon={FiBook} className="w-5 h-5 text-accent" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {nextWeek ? `Continue Week ${nextWeek.week}` : 'Review Modules'}
                    </span>
                  </Link>
                );
              })()}

              <Link to="/profile" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <SafeIcon icon={FiUsers} className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Update Profile</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;